import { FUNNEL_TYPES } from "../utils/contants";
import { t } from "i18next";
import { AuthenticationHeader } from "../../auth/components/AuthenticationHeader";
import {
  LoginModalBody,
  LoginModalFooter,
} from "../../auth/components/LoginModal";
import {
  SignUpModalBody,
  SignUpModalFooter,
} from "../../auth/components/SignUpModal";
import {
  SignUpWithEmailPasswordModalBody,
  SignUpWithEmailPasswordModalFooter,
} from "../../auth/components/SignUpWithEmailPasswordModal";
import { FunnelContextArea } from "../types";
import { SIGN_UP_FUNNEL_VALUE_PROPS } from "../../auth/constants";
import {
  BuildFunnelConfig,
  useBuildFunnel,
  UseBuildFunnelParams,
} from "./useBuildFunnel";
import { useAuthOrchestration } from "./useAuthOrchestration";

export function useAuthFunnel(params: UseBuildFunnelParams) {
  const { context, options, setFunnelConfig } = params;

  // Use orchestration for signup flows
  const { handleAuthSuccess } = useAuthOrchestration({
    contextArea: context?.modalContext || FunnelContextArea.navBar,
    onComplete: (result) => {
      // Close the modal — useBuildFunnel.onSuccess normally does this,
      // but handleAuthSuccess bypasses it for the login (non-signup) path.
      setFunnelConfig();
      options?.onSuccess?.(result);
    },
    skipOnboarding: options?.skipOnboarding ?? false,
    context,
    options,
    setFunnelConfig,
  });

  const buildFunnel = ({
    type,
    context,
    options,
    props,
  }: BuildFunnelConfig) => {
    let funnelHeader, funnelBody, funnelFooter;

    const funnelProps = {
      ...context,
      ...options,
      ...props,
      release: context?.purchaseRelease,
      artistId: context?.donationArtistId,
      // Use orchestrated callback for all auth funnels — SSO on login can create new accounts
      onSuccess: handleAuthSuccess,
    };

    const modalContext = context?.modalContext;
    const isStartNextReleaseContext =
      modalContext === FunnelContextArea.startNextReleaseButton;
    const isReaderContext =
      modalContext === FunnelContextArea.reader ||
      modalContext === FunnelContextArea.verticalReader;
    const isDetailsContext =
      modalContext === FunnelContextArea.comicDetails ||
      modalContext === FunnelContextArea.releaseDetails;
    const isBuyPreviewContext =
      modalContext === FunnelContextArea.readerPreviewBanner ||
      modalContext === FunnelContextArea.readerOverlay ||
      modalContext === FunnelContextArea.featureCard ||
      modalContext === FunnelContextArea.heroCard;
    const isValkyraeContext = modalContext === FunnelContextArea.valkyrae;

    const slimVersion =
      isDetailsContext ||
      isReaderContext ||
      isValkyraeContext ||
      isBuyPreviewContext;
    const showValueProps = !isStartNextReleaseContext && !isValkyraeContext;

    let headerText = "";
    if (type === FUNNEL_TYPES.login) {
      headerText = t("components.auth.login");
      funnelBody = <LoginModalBody {...funnelProps} />;
      funnelFooter = <LoginModalFooter />;
    } else if (type === FUNNEL_TYPES.signupEmailPassword) {
      headerText = t("components.auth.signUp");
      funnelBody = <SignUpWithEmailPasswordModalBody {...funnelProps} />;
      funnelFooter = <SignUpWithEmailPasswordModalFooter {...funnelProps} />;
    } else if (type === FUNNEL_TYPES.signup) {
      headerText = t("components.auth.signUp");

      const { valueProps: passedValueProps, welcomeText } = options || {};
      const { signUpValueProps, signUpWelcomeText, signUpHeader } =
        getFunnelTextForContext(modalContext);

      const valueProps = passedValueProps ?? signUpValueProps ?? [];
      headerText = signUpHeader ? signUpHeader : headerText;
      funnelBody = (
        <SignUpModalBody
          valueProps={showValueProps ? valueProps : []}
          welcomeText={welcomeText ?? signUpWelcomeText}
          triggeredByStartNextRelease={isStartNextReleaseContext}
          showGraphic={isStartNextReleaseContext}
          showEmailPasswordForm={slimVersion}
          centerWelcomeText={slimVersion}
          largeLoginCTA={isValkyraeContext}
          {...funnelProps}
        />
      );
      funnelFooter = (
        <SignUpModalFooter {...funnelProps} largeLoginCTA={isValkyraeContext} />
      );
    }

    funnelHeader = (
      <AuthenticationHeader
        text={isStartNextReleaseContext ? undefined : headerText}
        {...funnelProps}
      />
    );

    return {
      funnelHeader,
      funnelBody,
      funnelFooter,
    };
  };

  return useBuildFunnel(buildFunnel, params);
}

const getFunnelTextForContext = (contextArea?: FunnelContextArea) => {
  // For now value props are equal for all contexts
  const valueProps = SIGN_UP_FUNNEL_VALUE_PROPS.map((prop) =>
    t(prop, { defaultValue: "" }),
  ) as string[];

  let welcomeText;
  let header;
  switch (contextArea) {
    case FunnelContextArea.navBar:
      welcomeText = t("components.auth.welcomeText.createFreeAccount");
      break;
    case FunnelContextArea.followArtist:
      welcomeText = t("components.auth.welcomeText.getNotified");
      break;
    case FunnelContextArea.addToLibrary:
      welcomeText = t("components.auth.welcomeText.readTrackAndDiscover");
      break;
    case FunnelContextArea.donate:
      welcomeText = t("components.auth.welcomeText.stayConnected");
      break;
    case FunnelContextArea.startNextReleaseButton:
      header = t("components.auth.signUpHeader.saveYourReadingProgress");

      welcomeText = t(
        "components.auth.welcomeText.saveReadingProgressAndJoinCommunity",
      );
      break;
  }

  return {
    signUpValueProps: valueProps,
    signUpWelcomeText: welcomeText,
    signUpHeader: header,
  };
};
